import React, { FC, useState } from 'react'
import { CtaPopup } from 'src/components/molecules/ctaPopup'
import { PrismicCtaPopup } from 'src/typings/generated/graphql'
import { useCtaPopupSuppression } from 'src/utils/useCtaPopupSuppression'
import { useScrollTrigger } from 'src/utils/useScrollTrigger'

/**
 * The scroll threshold at which the CTA popup will be triggered.
 *
 * This is set to half the viewport height, meaning the popup will appear when the user has scrolled down half a
 * screen.
 */
const POPUP_ACTIVATION_SCROLL_THRESHOLD = '50vh'

/**
 * Props for the `CtaPopupZone` component.
 */
export type CtaPopupZoneProps = {
  ctaPopup?: PrismicCtaPopup
}

/**
 * Component that renders a CTA popup when the conditions are met.
 */
export const CtaPopupZone: FC<CtaPopupZoneProps> = ({ ctaPopup }) => {
  const [isPastScrollThreshold, setIsPastScrollThreshold] = useState(false)
  const { isSuppressed, suppress } = useCtaPopupSuppression(ctaPopup?.id)

  // Trigger the CTA popup when the user scrolls down the page.
  useScrollTrigger(POPUP_ACTIVATION_SCROLL_THRESHOLD, () => {
    if (!isPastScrollThreshold) {
      setIsPastScrollThreshold(true)
    }
  })

  const shouldRenderPopup =
    isPastScrollThreshold &&
    ctaPopup &&
    ctaPopup?.data?.is_enabled !== false &&
    !isSuppressed

  if (!shouldRenderPopup) {
    return null
  }

  return (
    <CtaPopup
      ctaPopupId={ctaPopup.uid}
      body={ctaPopup.data.body}
      headline={ctaPopup.data.headline}
      imageMode={ctaPopup.data.image_mode}
      image={{
        data: ctaPopup.data.image?.gatsbyImageData,
        alt: ctaPopup.data.image?.alt ?? '',
      }}
      ctaLabel={ctaPopup.data.cta_label}
      ctaLink={ctaPopup.data.cta_link}
      rejectButtonLabel={ctaPopup.data.reject_button_label}
      onClose={suppress}
    />
  )
}
